/**
 * Presentation helpers for a proposed next start number (NCR sets, pads and
 * books). The proposal is arithmetic-transparent by construction — every input
 * staff check is on the object — so this is pure formatting, no derivation:
 * the backend's `get_next_start_number` / `propose_reorder_start_number`
 * already did the arithmetic.
 */

import type { NextStartNumber } from "../data/api-types";

/** Where the proposal came from, as staff read it. */
export function startNumberSource(proposal: NextStartNumber): string {
  switch (proposal.basis) {
    case "order_line":
      return `from order line ${proposal.basis_ref}`;
    case "tharstern_job":
      return `from Tharstern job ${proposal.basis_ref}`;
    case "opening_number":
      return "the opening number, as entered";
  }
}

/** "last start 095751 + 500 → 096251": the brief's own worked phrasing.
 * Adds a "(qty N)" aside only when `run_length` differs from `last_quantity`
 * (a book or pad, whose sets per book are folded in). An opening number has
 * no arithmetic: null. */
export function formatStartNumberArithmetic(
  proposal: NextStartNumber,
): string | null {
  if (
    proposal.last_start == null ||
    proposal.run_length == null ||
    proposal.last_quantity == null
  )
    return null;
  const runLength = proposal.run_length.toLocaleString();
  const multiplierNote =
    proposal.run_length !== proposal.last_quantity
      ? ` (qty ${proposal.last_quantity.toLocaleString()})`
      : "";
  return `last start ${proposal.last_start} + ${runLength}${multiplierNote} → ${proposal.next_start}`;
}

/** The staleness warning, or null. Never a substitution: a newer Tharstern
 * job of the customer's may be another series (a false positive here is
 * intentional and harmless), and a CIS line of the same item is the series
 * moving on since the proposal's source. */
export function startNumberStalenessWarning(
  proposal: NextStartNumber,
): string | null {
  if (proposal.possibly_superseded_by_line_ref)
    return `Order line ${proposal.possibly_superseded_by_line_ref} already used numbers at or after ${proposal.next_start}. Check before confirming.`;
  if (proposal.possibly_superseded_by_job_no)
    return `Tharstern job ${proposal.possibly_superseded_by_job_no} used numbers at or after ${proposal.next_start}. Check which series before confirming.`;
  return null;
}
